export type HeroNavType = 'browse' | 'detail' | 'search'

export interface HeroSlide {
  id: string
  image_url: string
  headline: string
  description?: string
  cta_text?: string
  nav_type: HeroNavType
  nav_value: string
  tracking_id: string
}

export function getHeroSlideUrl(slide: HeroSlide): string {
  switch (slide.nav_type) {
    case 'browse':
      return `/browse?slug=${slide.nav_value}`
    case 'detail':
      return `/products/${slide.nav_value}`
    case 'search':
      return `/search?q=${encodeURIComponent(slide.nav_value)}`
    default:
      return '/'
  }
}

// Bento Grid Types
export type BentoNavType = 'browse' | 'detail' | 'search'

export interface BentoTrackingParams {
  pd_rd_w: string
  pd_rd_wg: string
  pd_rd_r: string
}

export interface BentoGridItem {
  id: string
  title: string
  image: string
  ctaText: string
  navType: BentoNavType
  navValue: string
  trackingParams: BentoTrackingParams
}

export function getBentoItemUrl(item: BentoGridItem): string {
  const { navType, navValue, id, trackingParams } = item
  const { pd_rd_w, pd_rd_wg, pd_rd_r } = trackingParams

  let basePath: string
  switch (navType) {
    case 'browse':
      basePath = `/browse?slug=${navValue}`
      break
    case 'detail':
      basePath = `/products/${navValue}`
      break
    case 'search':
      basePath = `/search?q=${encodeURIComponent(navValue)}`
      break
    default:
      basePath = '/'
  }

  const separator = basePath.includes('?') ? '&' : '?'
  return `${basePath}${separator}pd_rd_w=${pd_rd_w}&pd_rd_wg=${pd_rd_wg}&pd_rd_r=${pd_rd_r}&ref=home_bento_${id}`
}

// Product Discovery Types
export type DiscoveryLayoutType = 'grid' | 'carousel'

export interface Product {
  id: string
  slug: string
  name: string
  rating: number
  reviews: number
  currentPrice: number
  originalPrice?: number
  image: string
  badgeText?: string
  ageRestricted?: boolean
  discountEndsAt?: string // ISO date string - shows countdown if within 24 hours
  soldCount?: number // e.g., "20K+ bought in past month"
  stockCount?: number // 0 = out of stock, >0 = actual stock available
  brand?: string
  category?: string
  merchantName?: string
  discountPercentage?: number
  vatIncluded?: boolean
  description?: string
}

export interface ProductDiscoveryConfig {
  title: string
  widgetType: string
  defaultLayout: DiscoveryLayoutType
  showLayoutToggle?: boolean
  viewAllLink?: string
}

export function getProductUrl(product: Product, widgetType: string): string {
  return `/products/${product.slug}?ref=hp_discovery&pd_rd_w=${widgetType}`
}

import type { ProductSummary } from '../product/product.types'

export function mapApiProductToUI(p: ProductSummary): Product {
  const basePrice = Number.parseFloat(p.price || '0')
  let currentPrice = basePrice
  let originalPrice: number | undefined

  if (p.promo_price && p.promo_price !== '0.00') {
    currentPrice = Number.parseFloat(p.promo_price)
    originalPrice = basePrice
  } else if (p.discount_percentage && p.discount_percentage > 0) {
    currentPrice = basePrice * (1 - p.discount_percentage / 100)
    originalPrice = basePrice
  }

  return {
    id: p.product_slug,
    slug: p.product_slug,
    name: p.name,
    rating: p.average_rating,
    reviews: p.review_count,
    currentPrice,
    originalPrice,
    image: p.main_thumbnail || '',
    badgeText: p.in_campaign ? p.campaign_name : undefined,
    // Use availability boolean as primary; if false, stock is 0. If true/missing, use stockleft or default to 1
    stockCount: p.availability === false ? 0 : (p.stockleft ?? 1),
    merchantName: p.merchant_name,
    discountPercentage: p.discount_percentage,
    vatIncluded: p.vat_included,
    description: p.description,
  }
}

export function calculateDiscount(currentPrice: number, originalPrice?: number): number | null {
  if (!originalPrice || originalPrice <= currentPrice) return null
  return Math.round(((originalPrice - currentPrice) / originalPrice) * 100)
}
